import * as amplitude from "@amplitude/analytics-browser";
import classNames from "classnames";
import { memo, ReactElement, useContext } from "react";
import { useLocation } from "react-router-dom";
import { Flex } from "../../../flex";
import { Text } from "../../../text";
import { Tooltip } from "../../../tooltip/tooltip";
import { NavIconSwap } from "../common/redo-nav-icon-swap";
import * as navigationCss from "../redo-navigation.module.css";
import { PrimaryNavIconOnlyContext, PrimaryTab } from "./redo-primary-nav";

export interface PrimaryNavigationTabProps {
  tab: PrimaryTab;
  selectedTabId?: string;
  manuallySelected?: boolean;
  sectionType?: "core" | "active" | "inactive";
}

export const PrimaryNavigationTab = memo(function PrimaryNavigationTab({
  tab,
  selectedTabId,
  manuallySelected,
  sectionType,
}: PrimaryNavigationTabProps) {
  const { UnfilledIcon, FilledIcon, hasNotification, type } = tab;
  const location = useLocation();
  const iconOnly = useContext(PrimaryNavIconOnlyContext);

  const isSelected =
    (selectedTabId !== undefined && tab.id === selectedTabId) ||
    !!manuallySelected;

  const as = type === "link" ? "a" : "button";

  // Matches both the legacy `/order-management/returns/...` (still hit via
  // back-compat redirects) and new `/returns/returns/...` return detail URLs.
  const isOnReturnDetailsPage =
    /\/(order-management|returns)\/returns\/[^/]+\/[^/]+$/.test(
      location.pathname,
    );

  const tabElement: ReactElement = (
    <Flex
      align="center"
      as={as}
      className={classNames(
        navigationCss.primaryTab,
        isSelected && navigationCss.selected,
      )}
      data-dd-action-name={tab["data-dd-action-name"]}
      data-testid={`primary-nav-${tab.id}`}
      dir="column"
      gap="xxs"
      justify="center"
      linkProps={tab.type === "link" ? { to: tab.href } : undefined}
      onClick={() => {
        amplitude.track("nav/sidebar-item-clicked", {
          section: tab.id,
          item: tab.id,
          sectionType: sectionType ?? "other",
        });
        if (tab.type === "button") {
          tab.onClick();
        }
      }}
      w="full"
      {...(tab.id === "help" &&
        tab.type === "button" &&
        !isOnReturnDetailsPage && { "data-intercom-target": "" })}
    >
      <div className={navigationCss.primaryTabIconBox}>
        {hasNotification && <span className={navigationCss.notificationDot} />}
        <NavIconSwap
          FilledIcon={FilledIcon}
          isSelected={isSelected}
          size={20}
          UnfilledIcon={UnfilledIcon}
        />
      </div>
      {!iconOnly && !tab.hideLabel && (
        <Text
          as="span"
          fontSize="xxs"
          textAlign="center"
          textColor={isSelected ? "primary" : "secondary"}
        >
          {tab.friendlyName}
        </Text>
      )}
    </Flex>
  );

  // When the label isn't shown inline — either because the whole nav
  // is in icon-only mode or this specific tab opted out via
  // `tab.hideLabel` — wrap the tab in a hover tooltip so users can
  // still identify each icon.
  if (iconOnly || tab.hideLabel) {
    return (
      <Tooltip darkMode placement="right" title={tab.friendlyName}>
        {tabElement}
      </Tooltip>
    );
  }

  return tabElement;
});
